import type {
  AgendaDay,
  AgendaTask,
  TaskInstanceState,
  TaskInstanceView,
} from '@amumin/api-client';
import { belongsToDay } from '@amumin/domain';

// Taken per lid (DESIGN.md §8.W1/W5): welke instance hoort bij wie, en hoeveel is er klaar.
// `together`/`first` hebben één gedeelde instance (member_id null). Getest in tasks.test.ts.

/**
 * Taken van één dag (Vandaag-kaart, Taken Vandaag). Een taak hoort bij zijn eigen dag
 * (fix/taken-per-dag): wat op een eerdere dag open bleef, komt hier nooit als tweede exemplaar
 * terug (dus ook niet dubbel in de voortgang).
 */
export function tasksForDay(days: ReadonlyArray<AgendaDay>, key: string): AgendaTask[] {
  const day = days.find((d) => d.date === key);
  return day ? day.tasks.filter((task) => belongsToDay(task, key)) : [];
}

export function instanceFor(task: AgendaTask, memberId: string): TaskInstanceView | null {
  const list = task.instances ?? [];
  return (
    list.find((i) => i.member_id === memberId) ?? list.find((i) => i.member_id === null) ?? null
  );
}

/** Toestand van een taak voor één lid (valt terug op S1-velden zonder instances). */
export function stateFor(task: AgendaTask, memberId: string): TaskInstanceState {
  const inst = instanceFor(task, memberId);
  if (inst) {
    // Bij "wie het eerst": een ander was eerder → voor dit lid telt hij als klaar.
    return inst.state;
  }
  if (task.done_member_ids.includes(memberId)) return 'done';
  return task.state ?? 'open';
}

export function isDoneState(state: TaskInstanceState): boolean {
  return state === 'done' || state === 'skipped';
}

export interface Progress {
  done: number;
  total: number;
}

/** Voortgang per lid op één dag; alleen leden met minstens één taak. */
export function progressByMember(tasks: ReadonlyArray<AgendaTask>): Map<string, Progress> {
  const out = new Map<string, Progress>();
  for (const task of tasks) {
    for (const memberId of task.member_ids) {
      const p = out.get(memberId) ?? { done: 0, total: 0 };
      const state = stateFor(task, memberId);
      if (state !== 'skipped') {
        p.total += 1;
        if (state === 'done') p.done += 1;
      }
      out.set(memberId, p);
    }
  }
  return out;
}

/** Taken van één lid, open eerst, daarna op dagdeel en volgorde. */
export function tasksOf(tasks: ReadonlyArray<AgendaTask>, memberId: string): AgendaTask[] {
  const order = { morning: 0, afternoon: 1, evening: 2 } as const;
  return tasks
    .filter((t) => t.member_ids.includes(memberId))
    .sort((a, b) => {
      const da = isDoneState(stateFor(a, memberId)) ? 1 : 0;
      const db = isDoneState(stateFor(b, memberId)) ? 1 : 0;
      if (da !== db) return da - db;
      const ta = a.time_of_day ? order[a.time_of_day] : -1;
      const tb = b.time_of_day ? order[b.time_of_day] : -1;
      if (ta !== tb) return ta - tb;
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
}

/** Sleutel van een taak-instance voor de API: serie (task.id) + original_date. */
export function taskRef(task: AgendaTask, day: string): { series_id: string; date: string } {
  return { series_id: task.id, date: task.original_date ?? day };
}
